'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { useRouter } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { NrsusdtpCurrency } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { DynamicEarnEvent } from '@flipster/utils/segment/events/dynamic-earn';

import { useLoginKycGuard } from '../../../../shared/login-kyc-gurad';
import { AprModal } from '../../../shared/apr-modal';
import { narsilDetailsBunja, narsilOverviewBunja, narsilRedeemOverviewBunja } from '../../../shared/state';
import { useNrsusdtpRedeemModal } from '../../../shared/subscribe-redeem-flow/redeem-modal';
import { useNrsusdtpSubscribeModal } from '../../../shared/subscribe-redeem-flow/subscribe-modal';
import { ActiveCard } from './active-card';
import { InactiveCard } from './inactive-card';

const narsilProductName = 'Narsil USDT Prime';
const narsilDetailsPath = '/dynamic-earn/details/NRSUSDTP';

export const NarsilEarningsCard = () => {
  const router = useRouter();
  const overlay = useOverlay();
  const guardLoginAndKyc = useLoginKycGuard();
  const openSubscribeModal = useNrsusdtpSubscribeModal();
  const openRedeemModal = useNrsusdtpRedeemModal();

  const { overviewAtom, hasPendingRequestAtom, productCollateralRateAtom } = useBunja(narsilOverviewBunja);
  const { hasAvailableBalanceAtom: hasRedeemAvailableBalanceAtom } = useBunja(narsilRedeemOverviewBunja);
  const { detailsAtom } = useBunja(narsilDetailsBunja);

  const overview = useAtomValue(overviewAtom);
  const hasPendingRequest = useAtomValue(hasPendingRequestAtom);
  const hasRedeemAvailableBalance = useAtomValue(hasRedeemAvailableBalanceAtom);
  const collateralRate = useAtomValue(productCollateralRateAtom);
  const details = useAtomValue(detailsAtom);

  const hasSubscribedBalance = Num(overview?.subscribedBalance ?? '0').gte('0.01');
  const isActive = !overview?.isFirstTimeUser && (hasSubscribedBalance || hasPendingRequest);

  const onAprInfoClick = () => {
    if (!overview) return;
    overlay.open(({ isOpen, close, unmount }) => (
      <AprModal isOpen={isOpen} onClose={close} onExit={unmount} overview={overview} />
    ));
  };

  if (isActive) {
    return (
      <ActiveCard
        overview={overview}
        currency={NrsusdtpCurrency}
        productName={narsilProductName}
        detailsPath={narsilDetailsPath}
        collateralRate={collateralRate}
        hasPendingRequest={hasPendingRequest}
        hasRedeemAvailableBalance={hasRedeemAvailableBalance}
        onSubscribe={openSubscribeModal}
        onRedeem={openRedeemModal}
        onAprInfoClick={onAprInfoClick}
      />
    );
  }

  return (
    <InactiveCard
      overview={overview}
      currency={NrsusdtpCurrency}
      productName={narsilProductName}
      detailsPath={narsilDetailsPath}
      tvl={details?.totalValueLocked}
      collateralRate={collateralRate}
      onExplore={() =>
        guardLoginAndKyc(() => {
          segment.track(DynamicEarnEvent.DynamicEarnProductDetailPageClick, {
            button_type: 'earn_while_trading',
          });
          router.push(narsilDetailsPath);
        })
      }
      onAprInfoClick={onAprInfoClick}
    />
  );
};
